/**
 * TrainingBreakdownWidget — training assigned vs completed, per topic.
 *
 * "Topic" is the course title; the selector also offers the course's category
 * (the EHS-vs-other split), delivery type and department. All four live on
 * `training_courses` / `training_records` — nothing here invents a topic list, so
 * a tenant that renames a course sees the new name immediately.
 *
 * Two series rather than one bar, because a count alone cannot be read: 40
 * completions is good news against 45 assigned and bad news against 400. The
 * stack always sums to what was assigned, since Outstanding is computed as
 * assigned − completed rather than counted separately.
 *
 * Honesty detail the chart cannot carry: a record whose course row has been
 * deleted has no topic to sit under, so it is excluded from the bars. The
 * coverage line reports how many, instead of letting shorter bars imply less
 * training.
 *
 * Click a bar → the training records behind it.
 */
import React, { useState } from 'react';
import { useDashboard } from '../context/DashboardContext';
import { useDashboardData } from '../hooks/useDashboardData';
import { fetchTrainingBreakdown } from '../api/client';
import ChartWidget from './ChartWidget';
import DrilldownModal from './DrilldownModal';
import { CARD, HEADING } from '../ui';

/** Group choices offered in the widget. Keys must match the backend allow-list
 *  (analytics_ehs.TRAINING_GROUPS) — an unknown key is a 400, not a silent
 *  fallback, so the two lists are deliberately short and explicit. */
const GROUPS = [
  { key: 'topic', label: 'Topic' },
  { key: 'category', label: 'Category' },
  { key: 'training_type', label: 'Delivery' },
  { key: 'department', label: 'Department' },
];

export default function TrainingBreakdownWidget({
  title = 'Training by Topic',
  groupBy = 'topic',
  limit = 12,
  height,
  allowRegroup = true,
}) {
  const { siteId, time, departmentId, dimensions, fyStartMonth, refreshMs } = useDashboard();
  const [group, setGroup] = useState(groupBy);
  const [drill, setDrill] = useState(null);

  const timeKey = time ? JSON.stringify(time) : '';
  const dimsKey = dimensions ? JSON.stringify(dimensions) : '';

  const { data, loading, error } = useDashboardData(
    (signal) => {
      const body = { group_by: group, limit };
      if (siteId) body.site_id = siteId;
      if (departmentId) body.department_id = departmentId;
      if (dimensions && Object.keys(dimensions).length) body.dimensions = dimensions;
      if (time) body.time = time;
      if (fyStartMonth) body.fy_start_month = fyStartMonth;
      return fetchTrainingBreakdown(body, { signal });
    },
    [siteId, timeKey, departmentId, dimsKey, group, limit, fyStartMonth],
    { refreshMs },
  );

  const rows = data?.data || [];
  const totals = data?.totals;
  const coverage = data?.coverage;
  const nothing = !loading && !error && rows.length === 0;

  return (
    <>
      <div className={`${CARD} p-4`}>
        <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
          <h3 className={HEADING}>{title}</h3>
          {allowRegroup && (
            <div className="flex shrink-0 flex-wrap gap-1">
              {GROUPS.map((g) => (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => setGroup(g.key)}
                  className={`rounded px-2 py-1 text-xs font-medium ${
                    group === g.key
                      ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-200'
                      : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                  }`}
                >
                  {g.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {nothing ? (
          <div className="py-10 text-center text-sm text-gray-400">
            {coverage?.records
              ? `No course details on the ${coverage.records} training record${
                coverage.records === 1 ? '' : 's'} in this period.`
              : 'No training assigned for the selected site, period and filters.'}
          </div>
        ) : (
          <ChartWidget
            type="hbar"
            data={rows}
            xKey="label"
            series={data?.series || []}
            stacked
            height={height || Math.max(240, rows.length * 34 + 50)}
            showValues
            loading={loading}
            error={error}
            onPointClick={(d) => {
              const row = d?.payload ?? d;
              const key = row?.key;
              if (key === undefined || key === null) return;
              setDrill({
                key,
                label: row.label,
                // The courses behind this bar. The bars group by a course
                // attribute the record table does not carry, so the drill
                // filters on the course ids instead of the group label.
                courseIds: row.course_ids || [],
              });
            }}
          />
        )}

        {!loading && !error && rows.length > 0 && (
          <p className="mt-2 text-[11px] leading-relaxed text-gray-500">
            {totals && (
              <>
                {totals.completed} of {totals.assigned} assigned training
                {totals.assigned === 1 ? '' : 's'} completed
                {totals.outstanding > 0 && ` — ${totals.outstanding} outstanding`}.
              </>
            )}
            {coverage && coverage.no_course > 0 && (
              <>
                {' '}
                {coverage.no_course} record
                {coverage.no_course === 1 ? '' : 's'} whose course has been removed
                {coverage.no_course === 1 ? ' is' : ' are'} not shown, having no
                topic to group under.
              </>
            )}
            {data?.truncated && ' Only the largest groups are charted.'}
          </p>
        )}
      </div>

      {/* Drills to the records behind the clicked bar only. Topic and category
          live on the course rather than the record, so the filter is on the
          course ids that make up the bar — training_records.course_id, exposed
          as the `course` drill dimension. Department filters on the record's own
          column. */}
      <DrilldownModal
        open={!!drill}
        metric={data?.drill?.metric || 'training.assigned'}
        title={`Training — ${drill?.label ?? ''}`}
        subtitle={
          group === 'department'
            ? 'Training records for this department. Click a row to open the record.'
            : 'Training records for this group only. Click a row to open the record.'
        }
        extraDimensions={
          drill
            ? (group === 'department'
              ? { department: [drill.key] }
              // No ids means no course rows behind the bar; send an
              // unmatchable filter rather than none, which would open every
              // record and misrepresent the click.
              : { course: drill.courseIds?.length ? drill.courseIds : ['__none__'] })
            : null
        }
        onClose={() => setDrill(null)}
      />
    </>
  );
}
